import { env } from "./env";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

type Init = Omit<RequestInit, "body"> & {
  body?: unknown;
  auth?: boolean;
};

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${env.VITE_API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function request<T>(path: string, init: Init = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const wantAuth = init.auth !== false;

  if (wantAuth && accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const body =
    init.body === undefined
      ? undefined
      : typeof init.body === "string"
        ? init.body
        : JSON.stringify(init.body);

  if (body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const doFetch = () =>
    fetch(`${env.VITE_API_URL}${path}`, {
      ...init,
      body,
      headers,
      credentials: "include",
    });

  let res = await doFetch();

  if (res.status === 401 && wantAuth) {
    const ok = await refreshAccessToken();
    if (ok && accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
      res = await doFetch();
    }
  }

  if (!res.ok) {
    let message = res.statusText;
    try {
      const err = (await res.json()) as { error?: string };
      if (err.error) message = err.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) return undefined as T;
  return (await res.json()) as T;
}

export function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export const api = {
  get: <T>(path: string, init?: Init) => request<T>(path, { ...init, method: "GET" }),
  post: <T>(path: string, body?: unknown, init?: Init) =>
    request<T>(path, { ...init, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, init?: Init) =>
    request<T>(path, { ...init, method: "PATCH", body }),
  delete: <T>(path: string, init?: Init) => request<T>(path, { ...init, method: "DELETE" }),
};
